import { useCallback, useMemo } from 'react';
import {
  claimDocVariableOntoCampaign,
  presentDocVariable,
  variableNameKey,
} from '../../../../utils/variableScope';

/** Paint This-doc rows with this campaign's value; name-conflict warnings. */
export default function useCampaignDocOverlay({
  hasCampaignScope,
  campaign,
  allSprints,
  campaignRows,
  campaignRowsRef,
  onCampRef,
  inheritedRows,
  higherScopeNames,
  missionVariables = null,
}) {
  const claimDocVariable = useCallback(
    (variable, patch) => {
      if (!onCampRef.current) return;
      const next = claimDocVariableOntoCampaign(
        campaignRowsRef.current,
        variable,
        patch
      );
      campaignRowsRef.current = next;
      onCampRef.current(next);
    },
    [campaignRowsRef, onCampRef]
  );

  const ancestorNameKeys = useMemo(
    () => new Set(inheritedRows.map((v) => variableNameKey(v.name)).filter(Boolean)),
    [inheritedRows]
  );

  const paintEditableRow = useCallback(
    (variable, scope) => {
      if (scope !== 'doc') return variable;
      const liveCampaign = campaign
        ? { ...campaign, variables: campaignRows }
        : null;
      return presentDocVariable(variable, {
        campaign: hasCampaignScope ? liveCampaign : campaign,
        allSprints,
        missionVariables,
      });
    },
    [hasCampaignScope, campaign, campaignRows, allSprints, missionVariables]
  );

  const conflictFor = useCallback(
    (variable, scope) => {
      const key = variableNameKey(variable.name);
      if (!key) return null;
      if (
        scope === 'doc' &&
        hasCampaignScope &&
        !ancestorNameKeys.has(key)
      ) {
        return null;
      }
      const conflictMap =
        scope === 'doc'
          ? higherScopeNames.forDoc
          : higherScopeNames.forCampaign;
      return conflictMap.get(key) || null;
    },
    [hasCampaignScope, ancestorNameKeys, higherScopeNames]
  );

  return { claimDocVariable, paintEditableRow, conflictFor };
}
